import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    InputSignal,
    OnInit,
    output,
    OutputEmitterRef,
    signal,
    WritableSignal,
} from '@angular/core';
import { map as radashMap } from 'radash';
import { firstValueFrom } from 'rxjs';

import { AppLogoComponent } from '@connect-services/client/components/app-logo/app-logo.component';
import { OrganizationAccessPermissionsService } from '@connect-services/client/services/organization-access-permissions.service';
import { SubscriptionService } from '@connect-services/client/services/subscription.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { License, LicensesService, Subscription } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

type LicenseCheckState = {
    application: License.ApplicationEnum;
    isAvailable: boolean;
    isChecked: boolean;
    license: License | null;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AppLogoComponent, CheckboxComponent, RessourcesModule],
    selector: 'app-licenses-select',
    templateUrl: './licenses-select.component.html',
})
export class LicensesSelectComponent implements OnInit {
    public readonly currentUserLicenses: InputSignal<License[] | undefined> = input<License[]>();
    public readonly licensesChecked: WritableSignal<LicenseCheckState[]> = signal([
        {
            application: License.ApplicationEnum.CONNECT_SERVICES,
            isAvailable: false,
            isChecked: false,
            license: null,
        },
        {
            application: License.ApplicationEnum.CONNECT_FIELD,
            isAvailable: false,
            isChecked: false,
            license: null,
        },
    ]);
    public onLicensesChange: OutputEmitterRef<License[]> = output<License[]>();

    protected readonly CheckboxState = CheckboxState;
    protected readonly organizationAccessPermissionsService = inject(OrganizationAccessPermissionsService);

    private readonly licensesService = inject(LicensesService);
    private readonly subscriptionService = inject(SubscriptionService);

    public changeSelection(application: License.ApplicationEnum): void {
        this.licensesChecked.set(
            this.licensesChecked().map((license: LicenseCheckState) =>
                license.application === application
                    ? {
                          ...license,
                          isChecked: !license.isChecked,
                      }
                    : license,
            ),
        );

        this.onLicensesChange.emit(
            this.licensesChecked()
                .filter((license: LicenseCheckState) => license.isChecked && license.license !== null)
                .map((license: LicenseCheckState) => license.license) as License[],
        );
    }

    public async ngOnInit(): Promise<void> {
        await this.getAvailableLicenses();

        if (!this.currentUserLicenses()) {
            return;
        }

        for (const licence of this.currentUserLicenses()!) {
            const updateLicencesChecked = this.licensesChecked().map((licenseChecked: LicenseCheckState) =>
                licenseChecked.application === licence.application
                    ? {
                          ...licenseChecked,
                          isAvailable: true,
                          isChecked: true,
                          license: licence,
                      }
                    : licenseChecked,
            );

            this.licensesChecked.set(updateLicencesChecked);
        }
    }

    private async getAvailableLicenses(): Promise<void> {
        const subscription = await this.subscriptionService.getSubscription();

        const availableLicenses = (
            await radashMap(Object.values(License.ApplicationEnum), (application: License.ApplicationEnum) => {
                return this.loadAvailableLicenseByApplication(subscription, application);
            })
        ).flat();

        this.licensesChecked.set(
            this.licensesChecked().map((licenseState: LicenseCheckState) => ({
                ...licenseState,
                isAvailable: !!availableLicenses.find((license) => license.application === licenseState.application),
                license: availableLicenses.find((license) => license.application === licenseState.application) ?? null,
            })),
        );
    }

    private async loadAvailableLicenseByApplication(
        subscription: Subscription,
        application: License.ApplicationEnum,
    ): Promise<License[]> {
        const license = await firstValueFrom(
            this.licensesService.licenseControllerGet({
                application: application,
                assignedOnly: false,
                subscription: subscription.id,
                take: 1,
            }),
        );

        return license.items;
    }
}
